import { useState, type ReactElement } from 'react';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import type { SurveySummaryData } from 'src/redux/survey/survey.types';
import { SurveyStatus } from 'src/redux/survey/survey.types';
import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';

const COLLAPSE_CHAR_THRESHOLD = 320;

interface OverviewSidepanelSummaryProps {
  summary?: SurveySummaryData;
  showSummaries: boolean;
  surveyStatus?: SurveyStatus;
  isShare?: boolean;
  isGeneratingSummary?: boolean;
  isLoadingSummary?: boolean;
  hasCreativeAnswers?: boolean;
  onGenerateSummary?: () => void;
}

function hasSurveySummaryContent(summary?: SurveySummaryData): boolean {
  if (!summary) {
    return false;
  }
  return summary.summaries.length > 0
    || (summary.designSummaries?.length ?? 0) > 0
    || (summary.messagingSummaries?.length ?? 0) > 0;
}

function SummaryTextBlock({ text }: { text: string }): ReactElement {
  const isLong = text.length > COLLAPSE_CHAR_THRESHOLD;
  const [isOpen, setIsOpen] = useState(!isLong);

  if (!isLong) {
    return (
      <p className={cn(portalDs.bodySm, 'text-[#0D0F1A]')}>
        {text}
      </p>
    );
  }

  const preview = `${text.slice(0, COLLAPSE_CHAR_THRESHOLD).trimEnd()}…`;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <p className={cn(portalDs.bodySm, 'text-[#0D0F1A]')}>
        {isOpen ? text : preview}
      </p>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(portalDs.link, 'mt-1')}
        >
          {isOpen ? 'Show less' : 'Show more'}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent />
    </Collapsible>
  );
}

function SummarySection({
  label,
  items,
}: {
  label?: string;
  items: string[];
}): ReactElement | null {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {label ? (
        <h4 className={cn(portalDs.sectionLabel, 'mb-0')}>
          {label}
        </h4>
      ) : null}
      {items.map((item, index) => (
        <SummaryTextBlock key={`${label ?? 'summary'}-${index}`} text={item} />
      ))}
    </div>
  );
}

export function surveyHasCreativeAnswers(questions: CombinedQuestionStats[]): boolean {
  return questions.some((question) => question.answers.some((answer) => Boolean(answer.imageUrl)));
}

function OverviewSidepanelSummary({
  summary,
  showSummaries,
  surveyStatus,
  isShare = false,
  isGeneratingSummary = false,
  isLoadingSummary = false,
  hasCreativeAnswers = false,
  onGenerateSummary,
}: OverviewSidepanelSummaryProps): ReactElement {
  const isComplete = surveyStatus === SurveyStatus.Complete;
  const hasSummary = hasSurveySummaryContent(summary) && !isGeneratingSummary;
  const isGenerating = isGeneratingSummary || isLoadingSummary;
  const showCreativeSections = hasCreativeAnswers
    && ((summary?.designSummaries?.length ?? 0) > 0 || (summary?.messagingSummaries?.length ?? 0) > 0);

  return (
    <section
      aria-label="Survey summary"
      className="mb-6 rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-4"
      data-testid="overview-sidepanel-summary"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-[#2563EB]" aria-hidden="true" />
          <h3 className={portalDs.headingH3}>
            AI summary
          </h3>
        </div>
        {hasSummary && !isShare && onGenerateSummary ? (
          <PortalDsButton
            type="button"
            variant="ghost"
            size="sm"
            title="Regenerate summary"
            aria-label="Regenerate summary"
            onClick={onGenerateSummary}
            disabled={isGenerating}
          >
            <RefreshCw className={`size-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
          </PortalDsButton>
        ) : null}
      </div>

      {!showSummaries ? (
        <p className={portalDs.bodySm}>
          Summaries are available once the survey completes. Finish collecting responses to generate an AI summary of your results.
        </p>
      ) : null}

      {showSummaries && !hasSummary && !isGenerating && isComplete && !isShare ? (
        <div className="space-y-2">
          <p className={portalDs.bodySm}>
            Generate an AI summary of all survey responses.
          </p>
          <PortalDsButton
            type="button"
            variant="accent"
            size="sm"
            onClick={onGenerateSummary}
            disabled={!onGenerateSummary}
          >
            Generate summary
          </PortalDsButton>
        </div>
      ) : null}

      {showSummaries && !hasSummary && !isGenerating && isComplete && isShare ? (
        <p className={portalDs.bodySm}>
          No summary has been generated for this survey yet.
        </p>
      ) : null}

      {showSummaries && isGenerating ? (
        <div
          className={cn(portalDs.bodySm, 'flex items-center gap-2')}
          data-testid="overview-sidepanel-summary-generating"
        >
          <Loader2 className="size-4 animate-spin text-[#2563EB]" aria-hidden="true" />
          <span>Generating summary…</span>
        </div>
      ) : null}

      {showSummaries && hasSummary ? (
        <div className="space-y-4" data-testid="overview-sidepanel-summary-content">
          {showCreativeSections ? (
            <>
              <SummarySection label="Design" items={summary?.designSummaries ?? []} />
              <SummarySection label="Messaging" items={summary?.messagingSummaries ?? []} />
              {(summary?.summaries.length ?? 0) > 0 ? (
                <SummarySection label="Overall" items={summary?.summaries ?? []} />
              ) : null}
            </>
          ) : (
            <SummarySection items={summary?.summaries ?? []} />
          )}
        </div>
      ) : null}

      {showSummaries && surveyStatus === SurveyStatus.Live && !hasSummary && !isGenerating ? (
        <p className={portalDs.bodySm}>
          Your survey is live. A summary will be available once enough responses have been collected.
        </p>
      ) : null}
    </section>
  );
}

export default OverviewSidepanelSummary;
